import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight, CheckCircle2, ChevronDown, ArrowLeft, Share2, HelpCircle } from 'lucide-react';
import { getPostBySlugAsync, getRelatedPostsAsync } from '../../services/blogService';
import MarkdownRenderer from './MarkdownRenderer';
import './BlogPost.css';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [copied, setCopied] = useState(false);

  // Load post by slug dynamically
  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    getPostBySlugAsync(slug).then((foundPost) => {
      if (!isMounted) return;
      if (!foundPost) {
        navigate('/blog', { replace: true });
        return;
      }
      setPost(foundPost);
      getRelatedPostsAsync(slug, 3).then((rel) => {
        if (isMounted) setRelatedPosts(rel);
      });
      setLoading(false);
    });

    return () => { isMounted = false; };
  }, [slug, navigate]);

  // Set document title, meta description, canonical URL, and FAQ JSON-LD Schema
  useEffect(() => {
    if (!post) return;

    // Title & Meta
    document.title = post.metaTitle || `${post.title} | Watt & Wattle Blog`;
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement('meta');
      descTag.setAttribute('name', 'description');
      document.head.appendChild(descTag);
    }
    descTag.setAttribute('content', post.metaDescription || post.excerpt);

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://wattandwattle.com.au/${post.slug}`);

    // Inject FAQ Schema JSON-LD if present
    let faqSchemaScript = document.getElementById('blog-faq-schema');
    if (post.faqs && post.faqs.length > 0) {
      if (!faqSchemaScript) {
        faqSchemaScript = document.createElement('script');
        faqSchemaScript.id = 'blog-faq-schema';
        faqSchemaScript.type = 'application/ld+json';
        document.head.appendChild(faqSchemaScript);
      }
      const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': post.faqs.map(faq => ({
          '@type': 'Question',
          'name': faq.question,
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': faq.answer
          }
        }))
      };
      faqSchemaScript.textContent = JSON.stringify(schemaData, null, 2);
    } else if (faqSchemaScript) {
      faqSchemaScript.remove();
    }

    return () => {
      const scriptToRemove = document.getElementById('blog-faq-schema');
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [post]);

  if (loading || !post) return null;

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="blog-post-page">
      {/* ─── Post Header ─── */}
      <div className="blog-post-header">
        <div className="blog-post-container">
          {/* Breadcrumb */}
          <nav className="blog-breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={14} />
            <Link to="/blog">Blog</Link>
            <ChevronRight size={14} />
            <span className="current">{post.title}</span>
          </nav>

          <h1 className="post-title">{post.title}</h1>

          <div className="post-header-meta">
            <div className="author-info">
              <span className="author-name">{post.author}</span>
            </div>
            <span className="meta-divider">•</span>
            <div className="meta-item">
              <Calendar size={15} />
              <span>{new Date(post.date).toLocaleDateString('en-AU', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>

            <button className="share-btn" onClick={handleShare} aria-label="Share article">
              <Share2 size={16} />
              <span>{copied ? 'Copied Link!' : 'Share'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ─── Featured Image ─── */}
      <div className="post-featured-image-container">
        <div className="blog-post-container">
          <div className="featured-image-frame">
            <img src={post.featuredImage} alt={post.imageAlt || post.title} />
          </div>
        </div>
      </div>

      {/* ─── Main Content ─── */}
      <div className="blog-post-body-section">
        <div className="blog-post-container">
          <div className="blog-post-layout">
            
            {/* Article Column */}
            <article className="blog-post-article">

              {/* Key Takeaways Box */}
              {post.keyTakeaways && post.keyTakeaways.length > 0 && (
                <div className="key-takeaways-card">
                  <div className="key-takeaways-header">
                    <CheckCircle2 size={22} className="takeaways-icon" />
                    <h3>Key Takeaways</h3>
                  </div>
                  <ul className="takeaways-list">
                    {post.keyTakeaways.map((point, idx) => (
                      <li key={idx}>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Parsed Markdown Body */}
              <MarkdownRenderer content={post.rawContent} />

              {/* FAQs Accordion */}
              {post.faqs && post.faqs.length > 0 && (
                <section className="post-faqs-section">
                  <div className="faq-section-title">
                    <HelpCircle size={24} className="faq-icon" />
                    <h2>Frequently Asked Questions</h2>
                  </div>
                  <div className="faqs-accordion">
                    {post.faqs.map((faq, index) => {
                      const isOpen = openFaqIndex === index;
                      return (
                        <div key={index} className={`faq-item ${isOpen ? 'open' : ''}`}>
                          <button className="faq-question" onClick={() => toggleFaq(index)}>
                            <span>{faq.question}</span>
                            <ChevronDown size={20} className={`faq-chevron ${isOpen ? 'rotate' : ''}`} />
                          </button>
                          {isOpen && (
                            <div className="faq-answer">
                              <p>{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Article Footer */}
              <div className="article-post-footer">
                <div className="back-to-blog">
                  <Link to="/blog" className="back-btn">
                    <ArrowLeft size={16} />
                    <span>Back to all articles</span>
                  </Link>
                </div>
              </div>
            </article>

          </div>
        </div>
      </div>

      {/* ─── Related Articles Section ─── */}
      {relatedPosts.length > 0 && (
        <section className="related-articles-section">
          <div className="blog-post-container">
            <h2 className="related-title">Related Articles</h2>
            <div className="related-grid">
              {relatedPosts.map((relPost) => (
                <article key={relPost.slug} className="blog-card">
                  <Link to={`/${relPost.slug}`} className="blog-card-img-link">
                    <div className="blog-card-img-wrapper">
                      <img src={relPost.featuredImage} alt={relPost.imageAlt || relPost.title} loading="lazy" />
                    </div>
                  </Link>
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      <span className="meta-item">
                        <Calendar size={14} />
                        {new Date(relPost.date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    </div>
                    <h3 className="blog-card-title">
                      <Link to={`/${relPost.slug}`}>{relPost.title}</Link>
                    </h3>
                    <p className="blog-card-excerpt">{relPost.excerpt}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
